import { computed, isRef, ref, unref, watch } from 'vue';
import { useAptosV2Client } from '@/composables/useAptosV2Client';
import {
  MaybeRef,
  tryOnScopeDispose,
  tryOnUnmounted,
  useTimeoutPoll,
} from '@vueuse/core';
import { useStore, useTokensStore } from '@/store';
import { useCurrencyFormat } from '@/composables/useCurrencyFormat';
import { getFromCache } from '@/utils/cache';
import isEqual from 'lodash/isEqual';
import { useWalletPlugin } from '@/composables/useWalletPlugin';
import { RECALCULATION_BALANCE_TIME } from '@/constants';

export function useAccountBalance(
  address: MaybeRef<string | undefined>,
  tokenInitial: MaybeRef<string | undefined>,
) {
  const { getToken } = useTokensStore();

  const token = computed(() =>
    getToken(unref(tokenInitial))?.isFungibleAsset
      ? getToken(unref(tokenInitial))?.faType
      : unref(tokenInitial),
  );
  let firstFetchFlag = false;
  const isFirstFetching = ref(true);
  const isFetching = ref(true);
  const isExists = ref(false);
  const balance = ref(0);
  const { pause, resume } = useTimeoutPoll(
    watchBalance,
    RECALCULATION_BALANCE_TIME,
    {
      immediate: false,
    },
  );

  const client = useAptosV2Client();

  tryOnUnmounted(() => pause());
  tryOnScopeDispose(() => pause());

  async function watchBalance() {
    isFetching.value = true;
    await fetchBalance(unref(address), unref(token));
    isFetching.value = false;
  }

  async function fetchBalance(
    fnAddress?: string,
    fnToken?: string,
  ): Promise<{ balance: number; isExists: boolean } | undefined> {
    if (!fnAddress || !fnToken) {
      return;
    }

    if (firstFetchFlag) {
      isFirstFetching.value = false;
    }

    firstFetchFlag = true;
    const cacheKey = `${fnAddress}-${fnToken}`;
    const moveValue = await getFromCache(
      cacheKey,
      async () => {
        let resultToken = fnToken;
        if (!resultToken.includes('::')) {
          const moveValue = (await client.client.view({
            payload: {
              function: `0x1::coin::paired_coin`,
              functionArguments: [resultToken],
            },
          })) as { vec: { account_address: string }[] }[];
          resultToken = moveValue[0]?.vec?.[0]?.account_address;
          if (resultToken === '0x1') resultToken = '0x1::aptos_coin::AptosCoin';
        }

        if (!resultToken) {
          const balances = await client.client.getCurrentFungibleAssetBalances({
            options: {
              where: {
                owner_address: {
                  _eq: fnAddress,
                },
                asset_type: {
                  _eq: fnToken,
                },
              },
            },
          });
          return [
            balances[0]?.amount || 0,
            !!balances[0]?.last_transaction_timestamp,
          ];
        }

        const isRegistered = await client.client.view({
          payload: {
            function: `0x1::coin::is_account_registered`,
            typeArguments: [resultToken],
            functionArguments: [fnAddress],
          },
        });
        const view = await client.client.view({
          payload: {
            function: `0x1::coin::balance`,
            typeArguments: [resultToken],
            functionArguments: [fnAddress],
          },
        });
        return [view[0], isRegistered[0]];
      },
      { time: 5000 },
    );
    const moveValueNumber = Number(moveValue[0]);
    const isRegistered = moveValue[1];
    if (cacheKey === `${unref(address)}-${unref(token)}`) {
      if (typeof moveValueNumber === 'number') {
        isExists.value = isRegistered;
        balance.value = +moveValueNumber;
      } else {
        isExists.value = false;
        balance.value = 0;
      }
    }

    return {
      balance: balance.value,
      isExists: isExists.value,
    };
  }

  async function refetch() {
    isFetching.value = true;
    const result = await fetchBalance(unref(address), unref(token));
    isFetching.value = false;

    return result;
  }

  resume();
  if (isRef(address) || isRef(token)) {
    watch(
      () => [unref(address), unref(token)],
      async (a, b) => {
        if (isEqual(a, b)) {
          return;
        }

        // Reset fetching state after arguments is changed
        pause();
        firstFetchFlag = false;
        isFirstFetching.value = true;
        isFetching.value = true;
        await fetchBalance(unref(address), unref(token)).then(() => {
          resume();
          isFetching.value = false;
        });
      },
    );
  } else {
    isFirstFetching.value = true;
    fetchBalance(unref(address), unref(token));
  }

  return {
    isFetching,
    isFirstFetching,
    isExists,
    balance,
    type: unref(token),
    refetch,
  };
}

export function useCurrentAccountBalance(
  token: MaybeRef<string | undefined>,
  options?: { useSuffix?: boolean; _decimals?: number },
) {
  const { account } = useWalletPlugin();
  const address = computed(() => account?.value?.address);
  const balance = useAccountBalance(address as unknown as string, token);
  const currencyFormat = useCurrencyFormat(balance.balance, token, options);

  return {
    ...balance,
    ...currencyFormat,
  };
}

export function useNativeBalance(
  address: MaybeRef<string | undefined>,
  options?: { useSuffix?: boolean },
) {
  const store = useStore();
  const token = computed(() => store.defaultToken.value);
  const balance = useAccountBalance(address, token);
  const currencyFormat = useCurrencyFormat(balance.balance, token, options);

  return {
    ...balance,
    ...currencyFormat,
  };
}

export function useCurrentNativeBalance(options?: { useSuffix?: boolean }) {
  const store = useStore();
  const token = computed(() => store.defaultToken.value);
  const balance = useCurrentAccountBalance(token);
  const currencyFormat = useCurrencyFormat(balance.balance, token, options);

  return {
    ...balance,
    ...currencyFormat,
  };
}
